import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { AIBorder } from '@atlassian/ai-components';
import { RovoThinkingIndicator } from '@atlassian/conversation-assistant-ui-components';

const disclaimerMessages = defineMessages({
	aiDisclaimerText: {
		id: 'comments-panel.summary.ai-disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Disclaimer text for AI-generated summary',
	},
	aiDisclaimerIconLabel: {
		id: 'comments-panel.summary.ai-disclaimer-icon-label',
		defaultMessage: 'Information',
		description: 'Label for the information icon in AI disclaimer',
	},
});

const collapsableContainerStyles = xcss({
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.0',
	paddingBottom: 'space.150',
	marginTop: 'space.negative.025',
});

const headerStyles = xcss({
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.150',
});

const pointerStyles = xcss({
	cursor: 'pointer',
});

const rootCollapsedStyles = xcss({
	height: '120px',
	overflow: 'hidden',
	// fade effect
	maskImage: 'linear-gradient(180deg, black 65%, transparent)',
	WebkitMaskImage: 'linear-gradient(180deg, black 65%, transparent)',
});

const rootExpandedStyles = xcss({
	minHeight: '120px',
});

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	cursor: 'default',
	width: '100%',
});

const loadingContentContainerStyles = xcss({
	minHeight: '207px',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
	paddingBlock: 'space.300',
	paddingInline: 'space.300',
});

const loadingSkeletonStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.150',
	marginTop: 'space.100',
});

const skeletonBarStyles = xcss({
	height: '12px',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
});

const customGrayShadowStyles = xcss({
	filter: 'blur(8px)',
	opacity: '0.15',
	background: 'color.blanket',
});

const grayBorderStyles = css({
	borderRadius: token('radius.large'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	overflow: 'hidden',
	position: 'relative',
});

const grayBorderInnerStyles = xcss({
	borderRadius: 'radius.large',
	backgroundColor: 'elevation.surface.overlay',
});

const disclaimerStyles = xcss({
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.100',
	paddingBottom: 'space.200',
});

type CommentPanelCollapsibleSummaryProps = {
	children: React.ReactNode;
	isCollapsed?: boolean;
	onExpandCollapse?: () => void;
	title: React.ReactNode;
	isLoading?: boolean;
};

export const CommentPanelCollapsibleSummary = forwardRef<
	HTMLDivElement,
	CommentPanelCollapsibleSummaryProps
>(({ children, isCollapsed, onExpandCollapse, title, isLoading = false }, forwardedRef) => {
	const intl = useIntl();
	const containerRef = useRef<HTMLButtonElement>(null);
	const [showLoading, setShowLoading] = useState(isLoading);

	// Show loading state for 3 seconds when isLoading becomes true
	useEffect(() => {
		if (isLoading) {
			setShowLoading(true);
			const timer = setTimeout(() => {
				setShowLoading(false);
			}, 3000);

			return () => clearTimeout(timer);
		}
	}, [isLoading]);

	const handleExpandCollapse = () => {
		onExpandCollapse?.();
	};

	const handleExpandCollapseButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		handleExpandCollapse?.();
	};

	const onContainerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const selection = window.getSelection()?.toString() ?? '';
		// Highlighting text should not expand the card
		const hasHighlightedText = selection.length > 0;
		// Clicking in a feedback modal or flag should not expand the card
		const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

		if (isCollapsed && !hasHighlightedText && isClickingOnContainer) {
			handleExpandCollapse?.();
		}
	};

	// Render loading state
	if (showLoading) {
		return (
			<AIBorder loading={true} customGlowStyles={customGrayShadowStyles}>
				<Box xcss={loadingContentContainerStyles}>
					<RovoThinkingIndicator title="Rovo is thinking" />
					<Box xcss={loadingSkeletonStyles}>
						<Box xcss={[skeletonBarStyles, xcss({ width: '100%' })]} />
						<Box xcss={[skeletonBarStyles, xcss({ width: '80%' })]} />
						<Box xcss={[skeletonBarStyles, xcss({ width: '60%' })]} />
						<Box xcss={[skeletonBarStyles, xcss({ width: '100%' })]} />
						<Box xcss={[skeletonBarStyles, xcss({ width: '80%' })]} />
					</Box>
				</Box>
			</AIBorder>
		);
	}

	return (
		<div css={grayBorderStyles}>
			<Box xcss={grayBorderInnerStyles}>
				<Pressable
					onClick={onContainerClick}
					ref={containerRef}
					xcss={[pressableStyles, isCollapsed && pointerStyles]}
				>
					<Inline alignBlock="center" spread="space-between" xcss={headerStyles}>
						{title}
						<IconButton
							appearance="subtle"
							color={token('color.icon.subtlest')}
							icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
							label={isCollapsed ? 'Expand' : 'Collapse'}
							spacing="compact"
							onClick={handleExpandCollapseButtonClick}
						/>
					</Inline>
					<Box
						ref={forwardedRef}
						xcss={[
							collapsableContainerStyles,
							isCollapsed ? rootCollapsedStyles : rootExpandedStyles,
						]}
						testId="comment-panel-collapse-container"
					>
						{children}
					</Box>
					<Box xcss={disclaimerStyles}>
						<Inline alignBlock="center" space="space.050">
							<InformationCircleIcon
								color={token('color.icon.subtlest')}
								label={intl.formatMessage(disclaimerMessages.aiDisclaimerIconLabel)}
								size="small"
							/>
							<Text color="color.text.subtlest">
								{intl.formatMessage(disclaimerMessages.aiDisclaimerText)}
							</Text>
						</Inline>
					</Box>
				</Pressable>
			</Box>
		</div>
	);
});
